import { Link } from "react-router-dom";
import { useState } from "react";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatINR } from "@/lib/api";
import { Flame, Plus } from "lucide-react";
import { toast } from "sonner";

const WEIGHTS = ["250g", "500g", "1kg"];

export default function ProductCard({ product }) {
  const { addItem, openCart } = useCart();
  const [weight, setWeight] = useState("500g");

  const handleAdd = () => {
    addItem(product, weight, 1);
    toast.success(`${product.name} (${weight}) added to cart`);
    openCart();
  };

  return (
    <article
      data-testid={`product-card-${product.slug}`}
      className="bg-white rounded-2xl border border-[#E6DEC3] overflow-hidden group hover:shadow-lg hover:border-[#D9822B]/60 transition-all duration-300 flex flex-col"
    >
      <Link to={`/product/${product.slug}`} data-testid={`product-link-${product.slug}`} className="block relative overflow-hidden aspect-[4/5] bg-[#F4EFE6]">
        <img
          src={product.image_url}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        {product.tag && (
          <Badge className="absolute top-3 left-3 bg-[#9E3B21] text-[#FBF8F3] hover:bg-[#9E3B21] rounded-full px-3 py-1 text-[10px] tracking-wider uppercase">
            {product.tag}
          </Badge>
        )}
        {product.spiciness && (
          <div className="absolute top-3 right-3 bg-white/90 backdrop-blur rounded-full px-2.5 py-1 flex items-center gap-1 text-[10px] font-medium text-[#9E3B21]">
            <Flame className="w-3 h-3" /> {product.spiciness}
          </div>
        )}
      </Link>

      <div className="p-5 flex-1 flex flex-col">
        <Link to={`/product/${product.slug}`}>
          <h3 className="font-serif text-xl font-semibold text-[#1E1B18] leading-tight group-hover:text-[#9E3B21] transition-colors">
            {product.name}
          </h3>
        </Link>
        <p className="text-xs text-[#6E675F] mt-1 uppercase tracking-wider">{product.subtitle}</p>

        <div className="flex gap-1.5 mt-4">
          {WEIGHTS.map((w) => (
            <button
              key={w}
              data-testid={`weight-option-${product.slug}-${w}`}
              onClick={(e) => { e.preventDefault(); setWeight(w); }}
              className={`flex-1 text-xs font-semibold py-1.5 rounded-full border transition-all ${
                weight === w
                  ? "bg-[#1E1B18] text-[#FBF8F3] border-[#1E1B18]"
                  : "bg-white text-[#1E1B18] border-[#E6DEC3] hover:border-[#9E3B21]"
              }`}
            >
              {w}
            </button>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="font-serif text-2xl font-semibold text-[#1E1B18]" data-testid={`product-price-${product.slug}`}>
            {formatINR(product.prices[weight])}
          </div>
          <Button
            data-testid={`add-to-cart-${product.slug}`}
            onClick={handleAdd}
            className="btn-primary rounded-full h-10 w-10 p-0"
            aria-label="Add to cart"
          >
            <Plus className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </article>
  );
}
